/**
 * PurchaseConfirmationModal - Two-step confirmation for credit/subscription purchase
 * 
 * Step 1: Confirm selection (before processing)
 * Step 2: Success or Cancellation notification
 * 
 * NO changes are made until user clicks "Confirm Purchase"
 */
import React from 'react';
import { CheckCircle, XCircle, X, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PurchaseConfirmationModal({ 
  isOpen, 
  onClose,
  onConfirm,
  onCancel,
  purchaseDetails,
  stage  // 'confirm' | 'success' | 'cancelled'
}) {
  if (!isOpen || !purchaseDetails) return null;
  
  const {
    type,           // 'bundle' | 'subscription_monthly' | 'subscription_annual'
    name,           // e.g., "Starter Pack" or "Pro Reader"
    credits,        // Number of credits to be granted
    price,          // Price in USD
    isAnnual,       // For subscriptions
    tierCode        // For subscriptions
  } = purchaseDetails;
  
  const isSubscription = type?.includes('subscription');
  
  // Stage 1: Confirm Selection (before processing)
  if (stage === 'confirm') {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onCancel()}
      >
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-purple-500/30 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
          {/* Header with Logo */}
          <div className="relative px-6 pt-6 pb-4 text-center border-b border-slate-700">
            <button 
              onClick={onCancel}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-700/50 transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
            
            {/* NewAudia Logo */}
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia" 
              className="h-12 mx-auto mb-3 rounded-lg"
            />
            <p className="text-xs text-slate-400">Confirm Your Purchase</p>
          </div>
          
          {/* Content */}
          <div className="px-6 py-6">
            {/* Cart Icon */}
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
              <ShoppingCart className="w-8 h-8 text-purple-400" />
            </div>
            
            <h2 className="text-xl font-bold text-white text-center mb-2">Confirm Your Selection</h2>
            <p className="text-slate-400 text-sm text-center mb-6">
              Please review your selection before completing the purchase.
            </p>
            
            {/* Purchase Details */}
            <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Plan</span>
                  <span className="text-white font-medium">{name}</span>
                </div>
                
                {isSubscription && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Billing</span>
                    <span className="text-white font-medium">
                      {isAnnual ? 'Annual' : 'Monthly'}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Price</span>
                  <span className="text-white font-medium">${price?.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center pt-2 border-t border-slate-700">
                  <span className="text-slate-400 text-sm">
                    {isSubscription && isAnnual ? "First Month's Credits" : "Credits You'll Receive"}
                  </span>
                  <span className="text-green-400 font-bold text-lg">+{credits} RC</span>
                </div>
                
                {isSubscription && (
                  <div className="pt-2 text-xs text-slate-500">
                    {isAnnual 
                      ? `You'll receive ${credits} credits monthly for 12 months. Subscription renews annually.`
                      : `You'll receive ${credits} credits monthly on your billing date.`
                    }
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={onCancel}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="cancel-purchase-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={onConfirm}
                className="flex-1 bg-purple-600 hover:bg-purple-500 text-white font-semibold"
                data-testid="confirm-purchase-btn"
              >
                Confirm Purchase
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Stage 2: Success Notification
  if (stage === 'success') {
    const {
      creditsIssued,
      newBalance,
      transactionId,
      subscriptionStartDate,
      subscriptionEndDate
    } = purchaseDetails;
    
    // Format dates if present
    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };
    
    const startDateFormatted = formatDate(subscriptionStartDate);
    const endDateFormatted = formatDate(subscriptionEndDate);
    
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-green-500/30 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
          {/* Header with Logo */}
          <div className="relative px-6 pt-6 pb-4 text-center border-b border-slate-700">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-700/50 transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
            
            {/* NewAudia Logo */}
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia" 
              className="h-12 mx-auto mb-3 rounded-lg"
            />
            <p className="text-xs text-slate-400">Purchase Complete</p>
          </div>
          
          {/* Content */}
          <div className="px-6 py-6 text-center">
            {/* Success Icon */}
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            
            <h2 className="text-xl font-bold text-white mb-2">Thank You!</h2>
            <p className="text-slate-300 text-sm mb-6">
              {isSubscription 
                ? `Welcome to ${name}! Your subscription is now active.`
                : 'Your purchase has been successfully processed.'
              }
            </p>
            
            {/* Purchase Summary */}
            <div className="bg-slate-800/50 rounded-xl p-4 mb-6 text-left">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">{name}</span>
                  <span className="text-white font-medium">${price?.toFixed(2)}</span>
                </div>
                
                {/* Credits Added */}
                <div className="flex justify-between items-center pt-2 border-t border-slate-700">
                  <span className="text-slate-400 text-sm">Credits Added</span>
                  <span className="text-green-400 font-bold">+{creditsIssued || credits} RC</span>
                </div>
                
                {/* New Balance */}
                {newBalance !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">New Balance</span>
                    <span className="text-purple-400 font-bold">{newBalance?.toFixed(2)} RC</span>
                  </div>
                )}
                
                {/* Subscription Dates */}
                {isSubscription && startDateFormatted && (
                  <>
                    <div className="pt-2 border-t border-slate-700">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-400 text-sm">Start Date</span>
                        <span className="text-white text-sm">{startDateFormatted}</span>
                      </div>
                      {endDateFormatted && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">Next Renewal</span>
                          <span className="text-white text-sm">{endDateFormatted}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* Transaction ID */}
                {transactionId && (
                  <div className="pt-2 border-t border-slate-700">
                    <div className="text-xs text-slate-500">
                      Transaction: {transactionId.slice(0, 20)}...
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Subscription Info */}
            {isSubscription && (
              <p className="text-xs text-slate-500 mb-4">
                Your credits will be refreshed monthly on your billing date.
                {isAnnual && ' You\'ve been charged for the full year.'}
              </p>
            )}
            
            {/* Affirm Button */}
            <Button
              onClick={onClose}
              className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold py-3"
              data-testid="success-close-btn"
            >
              Start Reading!
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Stage 3: Cancelled Notification
  if (stage === 'cancelled') {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-500/30 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
          {/* Header with Logo */}
          <div className="relative px-6 pt-6 pb-4 text-center border-b border-slate-700">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-700/50 transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
            
            {/* NewAudia Logo */}
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia" 
              className="h-12 mx-auto mb-3 rounded-lg"
            />
            <p className="text-xs text-slate-400">Purchase Cancelled</p>
          </div>
          
          {/* Content */}
          <div className="px-6 py-6 text-center">
            {/* Cancel Icon */}
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-500/20 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-slate-400" />
            </div>
            
            <h2 className="text-xl font-bold text-white mb-2">Purchase Cancelled</h2>
            <p className="text-slate-400 text-sm mb-6">
              No changes have been made to your account.
            </p>
            
            {/* Return Button */}
            <Button
              onClick={onClose}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3"
            >
              Return to Options
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
}
